package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@InitRoute
fun initResourcesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Resources"))
}

/** A link worth keeping: a club, a group, a channel, or a reference site. */
private data class Resource(
    val title: String
    , val description: String
    , val url: String
    , val tag: String
)

private val facebookGroups = listOf(
    Resource(
        "Lapidary Equipment & Supplies Marketplace"
        , "A diverse range of high-quality tools and materials for your lapidary pursuits. Buy, sell, and trade with fellow enthusiasts and professionals alike."
        , "https://www.facebook.com/groups/444584341870567"
        , "Group"
    )
    , Resource(
        "Shelton Rock & Mineral Society"
        , "For members of the society, people in the community interested in rocks and minerals or in joining the club, and rockhounds, lapidary artists, and collectors from around the world."
        , "https://www.facebook.com/groups/1102691563139199"
        , "Group"
    )
    , Resource(
        "Washington Agate and Mineral Society"
        , "Formed in 1937, WAMS of Olympia is the oldest rock club in Washington, affiliated with the Washington State Mineral Council and the Northwest Federation of Mineralogical Societies."
        , "https://www.facebook.com/p/Washington-Agate-and-Mineral-Society-100091451719746/"
        , "Page"
    )
)

private val youtubeVideos = listOf(
    Resource(
        "How to Identify Raw Agates: The \"Wax Luster\" Test"
        , "Most rockhounds walk right past good agates because they mistake them for ordinary flint. This is how to tell the difference in the field."
        , "https://www.youtube.com/watch?v=5Q1o04cIiwY&t=16s"
        , "Educational"
    )
    , Resource(
        "How to Identify Rocks"
        , "How can you tell what type of rock you have? A walk through telling one type from another."
        , "https://www.youtube.com/watch?v=cyBLmW5k06c&t=4s"
        , "Educational"
    )
    , Resource(
        "Learn Rock Identification with Two Geologists"
        , "Geologists Nat and Paul identify Lake Superior rocks on a walk down the beach, with short geology lessons along the way."
        , "https://www.youtube.com/watch?v=lWTgUbdccJE"
        , "Field trip"
    )
    , Resource(
        "Agates & Jasper: What Do You Really Know About Them?"
        , "A long look at what separates the two, and why the line between them is blurrier than most charts admit."
        , "https://www.youtube.com/watch?v=iPwB_1BMVOs&t=686s"
        , "Educational"
    )
    , Resource(
        "Rainy Day Roots"
        , "Pacific Northwest rockhounding — exploring, discovering, and preserving the legacy."
        , "https://www.youtube.com/@rainydayroots"
        , "Channel"
    )
)

private val localClubs = listOf(
    Resource(
        "Clallam County Gem & Mineral Association"
        , "A non-profit club in Clallam County teaching lapidary skills and promoting the hobby. Members learn to handle equipment and prepare stones in the club shop, and the club runs two annual gem shows in Port Angeles plus assorted field trips."
        , "https://www.sequimrocks.org/"
        , "Local"
    )
    , Resource(
        "Kitsap Mineral & Gem Society"
        , "A long-standing community of artists and rockhounds gathering to share the joy and the adventure of collecting gems and minerals."
        , "https://www.kmgsrockclub.com/"
        , "Nearby"
    )
    , Resource(
        "Maplewood Rock & Gem Club"
        , "Meets every third Monday of the month, all year long."
        , "https://maplewoodrockclub.com/"
        , "Nearby"
    )
    , Resource(
        "Mount Baker Rock & Gem Club"
        , "Meets the third Monday of each month at 7:00 pm in the Pioneer Pavilion Community Center, Ferndale."
        , "https://mtbakerrockclub.org/"
        , "Nearby"
    )
    , Resource(
        "Port Townsend Rock Club"
        , "Where individuals and families come together to cut rocks, polish stones, make jewelry, and create work in stone and metal. Beginners and old hands both welcome, with classes, workshops, and events."
        , "https://www.porttownsendrockclub.org/"
        , "Local"
    )
    , Resource(
        "Washington Agate and Mineral Society"
        , "The oldest rock club in Washington, formed in 1937. Membership is open to anyone with an interest in rocks and the lapidary arts."
        , "https://wamsolympia.com/"
        , "Local"
    )
)

private val referenceSites = listOf(
    Resource(
        "Rock Hounding"
        , "Find rocks, gems, and minerals near you."
        , "https://rockhounding.org/"
        , "Reference"
    )
    , Resource(
        "Rock Seeker"
        , "Cultivating curiosity about the natural world by helping enthusiasts of all ages find the wonder, history, art, and science in every stone."
        , "https://rockseeker.com/"
        , "Reference"
    )
    , Resource(
        "Washington State Department of Natural Resources"
        , "The state's own guidance on rockhounding in Washington — where it is allowed, and on what terms."
        , "https://dnr.wa.gov/washington-geological-survey/explore-popular-geology/rockhounding"
        , "Government"
    )
    , Resource(
        "RockTumbler.com"
        , "Everything you need for rock tumbling, including how to judge stream-rounded rough."
        , "https://rocktumbler.com/tips/stream-rounded-rough/"
        , "Reference"
    )
    , Resource(
        "911 Metallurgist"
        , "A dense mineral identification table, useful once you have narrowed things down."
        , "https://www.911metallurgist.com/blog/mineral-identification-table/"
        , "Reference"
    )
)

private val resourcesBySection = listOf(
    "Rock & Mineral Clubs" to localClubs
    , "Facebook Groups & Pages" to facebookGroups
    , "Videos & Channels" to youtubeVideos
    , "Websites Worth Knowing" to referenceSites
)

@Composable
private fun ResourcesSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun ResourceList(items: List<Resource>) {
    Ul(PageListStyle.toAttrs()) {
        items.forEach { item ->
            Li {
                Link(item.url, item.title)
                Text(" — ${item.description} ")
                Em { Text("(${item.tag})") }
            }
        }
    }
}

@Composable
private fun ResourcesHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Resources"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun ResourcesHero() {

    ResourcesHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/specimen_collection.png"
                    , alt = "A collection of rock and mineral specimens"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("The people, channels, and references behind most of what is on this site.") } }
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Clubs are the fastest way to learn this hobby. Someone who has been collecting the same creek for thirty years will tell you more in an afternoon than a month of reading will, and most clubs welcome visitors before you ever join. Nearly all of them run field trips, and many keep a lapidary shop that members can use.")
            }

            P {
                Text("What follows is the rest of it: the Facebook groups where material changes hands, the channels worth watching, and the reference sites kept open in a second tab. The clubs listed here are mostly Washington and Oregon ones; everything else applies wherever you collect.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        resourcesBySection.forEach { (section, items) ->
            ResourcesSectionTitle(section)
            ResourceList(items)
        }
    }
}

@Page("resources")
@Layout(".components.layouts.PageLayout")
@Composable
fun ResourcesPage() {
    ResourcesHero()
}
